import { flattenFoldersForPicker } from "@/lib/folders/folderTree";
import {
  findProviderFolderForSlot,
  navFolderSlot,
  pickBestFolderForSlot,
  type NavFolderSlot,
} from "@/lib/mail/folderNavSections";
import { isAureliaImapNamespace } from "@/lib/mail/aureliaImapFolders";
import { findAureliaFolderForSlot } from "@/lib/mail/aureliaImapFolders";
import {
  readAccountSettings,
  writeAccountSettings,
} from "@/lib/mail/accountSettingsPrefs";
import type { MailFolder } from "@/lib/types/mail";

const AURELIA_OVERRIDE_SLOTS: NavFolderSlot[] = [
  "sent",
  "drafts",
  "snoozed",
  "trash",
  "junk",
];

/** Dossiers IMAP reconnus comme « Envoyés » (doublons OVH : INBOX.Sent + Éléments envoyés…). */
export function listSentFolderCandidates(folders: MailFolder[]): MailFolder[] {
  const flat = flattenFoldersForPicker(folders);
  return flat.filter((folder) => navFolderSlot(folder.name, folder.label) === "sent");
}

export function sentFolderOverrideIsValid(
  folders: MailFolder[],
  override?: string | null
): boolean {
  const name = override?.trim();
  if (!name) return false;
  return flattenFoldersForPicker(folders).some((folder) => folder.name === name);
}

/**
 * En attente reste un dossier Aurelia.
 * Envoyés, brouillons, corbeille, indésirables suivent le dossier du compte.
 * Un ancien réglage Aurelia/ est retiré dès que le fournisseur a le sien.
 */
export function ensureAureliaFolderOverrides(
  accountId: number,
  folders: MailFolder[]
): boolean {
  const flat = flattenFoldersForPicker(folders);
  const settings = readAccountSettings(accountId);
  const next = { ...settings.folderSlotOverrides };
  let changed = false;

  for (const slot of AURELIA_OVERRIDE_SLOTS) {
    if (slot !== "snoozed") {
      const provider = findProviderFolderForSlot(flat, slot);
      if (provider) {
        const current = next[slot]?.trim();
        if (current && isAureliaImapNamespace(current)) {
          delete next[slot];
          changed = true;
        }
        continue;
      }
    }
    const aurelia = findAureliaFolderForSlot(flat, slot);
    if (!aurelia) continue;
    if (next[slot] !== aurelia.name) {
      next[slot] = aurelia.name;
      changed = true;
    }
  }

  if (!changed) return false;

  writeAccountSettings(accountId, {
    folderSlotOverrides: next,
  });
  return true;
}

/**
 * À la connexion ou après refresh IMAP : fixe Envoyés + slots Aurelia/.
 * Retourne le nom IMAP Envoyés choisi si un override a été écrit / forcé Aurelia.
 */
export function ensureSentFolderOverride(
  accountId: number,
  folders: MailFolder[]
): string | null {
  ensureAureliaFolderOverrides(accountId, folders);

  const flat = flattenFoldersForPicker(folders);
  const providerSent = findProviderFolderForSlot(flat, "sent");
  if (providerSent) {
    return providerSent.name;
  }
  const aureliaSent = findAureliaFolderForSlot(flat, "sent");
  if (aureliaSent) {
    return aureliaSent.name;
  }

  const candidates = listSentFolderCandidates(folders);
  if (candidates.length === 0) return null;

  const settings = readAccountSettings(accountId);
  const currentOverride = settings.folderSlotOverrides.sent?.trim();
  if (currentOverride && sentFolderOverrideIsValid(folders, currentOverride)) {
    return null;
  }

  const best = pickBestFolderForSlot("sent", candidates);
  if (!best) return null;

  writeAccountSettings(accountId, {
    folderSlotOverrides: {
      ...settings.folderSlotOverrides,
      sent: best.name,
    },
  });
  return best.name;
}

export function sentFolderSetupHint(folders: MailFolder[]): string | null {
  const flat = flattenFoldersForPicker(folders);
  if (findAureliaFolderForSlot(flat, "sent")) {
    return null;
  }
  const candidates = listSentFolderCandidates(folders);
  if (candidates.length <= 1) return null;
  const best = pickBestFolderForSlot("sent", candidates);
  if (!best) return null;
  const others = candidates
    .filter((f) => f.name !== best.name)
    .map((f) => f.label)
    .join(", ");
  return `Plusieurs dossiers « envoyés » détectés (${others}). Aurélia utilise « ${best.label} » pour l’affichage et les envois — modifiable ci-dessous.`;
}
